import { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, Power, Package, Search, Download, User, Calendar, X, FileSpreadsheet, ArrowRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Comodato = () => {
  const { api } = useAuth();
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [ativoFilter, setAtivoFilter] = useState('todos');
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({ nome: '', quantidade_total: 0, categoria: '', observacoes: '' });

  // Detail panel state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [alertas, setAlertas] = useState([]);

  const fetchItens = useCallback(async () => {
    try {
      const res = await api.get('/comodato/itens');
      setItens(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchAlertas = useCallback(async () => {
    try {
      const res = await api.get('/comodato/alertas');
      setAlertas(res.data?.alertas || []);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchItens(); fetchAlertas(); }, [fetchItens, fetchAlertas]);

  const filteredItens = useMemo(() => {
    let result = itens;
    if (ativoFilter === 'ativo') result = result.filter(i => i.ativo !== false);
    else if (ativoFilter === 'inativo') result = result.filter(i => i.ativo === false);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(i => i.nome?.toLowerCase().includes(term));
    }
    return result;
  }, [itens, searchTerm, ativoFilter]);

  const handleSaveItem = async () => {
    try {
      if (editingItem) {
        const res = await api.put(`/comodato/itens/${editingItem.id}`, itemForm);
        toast.success('Item atualizado');
        setItens(prev => prev.map(i => i.id === editingItem.id ? res.data : i));
      } else {
        const res = await api.post('/comodato/itens', itemForm);
        toast.success('Item criado');
        setItens(prev => [...prev, res.data]);
      }
      setShowItemDialog(false);
      setEditingItem(null);
      setItemForm({ nome: '', quantidade_total: 0, categoria: '', observacoes: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar item');
    }
  };

  const handleToggleAtivo = async (item) => {
    try {
      const res = await api.put(`/comodato/itens/${item.id}`, { ativo: !item.ativo });
      toast.success(res.data.ativo ? 'Item ativado' : 'Item desativado');
      setItens(prev => prev.map(i => i.id === item.id ? res.data : i));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Excluir "${item.nome}"?`)) return;
    try {
      await api.delete(`/comodato/itens/${item.id}`);
      toast.success('Item excluído');
      setItens(prev => prev.filter(i => i.id !== item.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setItemForm({ nome: item.nome, quantidade_total: item.quantidade_total, categoria: item.categoria || '', observacoes: item.observacoes || '' });
    setShowItemDialog(true);
  };

  const openNewDialog = () => {
    setEditingItem(null);
    setItemForm({ nome: '', quantidade_total: 0, categoria: '', observacoes: '' });
    setShowItemDialog(true);
  };

  const openDetail = async (item) => {
    setDetailItem(item);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await api.get(`/comodato/itens/${item.id}/detalhes`);
      setDetailData(res.data);
    } catch (err) {
      toast.error('Erro ao carregar detalhes');
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExportarItem = async (itemId) => {
    try {
      const res = await api.get(`/comodato/itens/${itemId}/exportar`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comodato_${detailItem?.nome?.replace(/\s+/g, '_') || 'item'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório exportado!');
    } catch (err) {
      toast.error('Erro ao exportar');
    }
  };

  // Summary stats
  const totalItens = itens.filter(i => i.ativo !== false).length;
  const totalEmUso = itens.reduce((sum, i) => sum + (i.em_uso || 0), 0);
  const totalDisponiveis = itens.reduce((sum, i) => sum + (i.disponiveis || 0), 0);

  const handleExportar = async () => {
    try {
      const res = await api.get('/comodato/exportar', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio_comodato.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório exportado!');
    } catch (err) {
      toast.error('Erro ao exportar relatório');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('pt-BR', { timeZone: 'America/Manaus' });
    } catch { return d?.slice(0, 10) || '—'; }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="comodato-title">Comodato</h1>
            <p className="text-muted-foreground text-sm">Equipamentos e materiais em empréstimo</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportar} className="gap-2" data-testid="btn-exportar-comodato">
              <Download className="h-4 w-4" />
              Exportar tudo
            </Button>
            <Button onClick={openNewDialog} className="gap-2" data-testid="btn-adicionar-comodato">
              <Plus className="h-4 w-4" />
              Adicionar item
            </Button>
          </div>
        </div>

        {/* Low-stock alerts */}
        {alertas.length > 0 && (
          <div className="space-y-2" data-testid="comodato-alertas">
            {alertas.map((alerta) => (
              <div
                key={alerta.item_id}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
                  alerta.severidade === 'critico'
                    ? 'bg-red-50 border-red-300 dark:bg-red-950/20 dark:border-red-800'
                    : 'bg-amber-50 border-amber-300 dark:bg-amber-950/20 dark:border-amber-800'
                }`}
                data-testid={`comodato-alerta-${alerta.item_id}`}
              >
                <AlertTriangle className={`h-4 w-4 shrink-0 ${
                  alerta.severidade === 'critico' ? 'text-red-600' : 'text-amber-600'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    alerta.severidade === 'critico' ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'
                  }`}>
                    {alerta.item_nome}
                  </p>
                  <p className={`text-xs ${
                    alerta.severidade === 'critico' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
                  }`}>
                    {alerta.mensagem}
                  </p>
                </div>
                <Badge variant="outline" className={`shrink-0 ${
                  alerta.severidade === 'critico' ? 'border-red-400 text-red-700' : 'border-amber-400 text-amber-700'
                }`}>
                  {alerta.disponiveis}/{alerta.quantidade_total}
                </Badge>
              </div>
            ))}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Total de Itens</p>
                <p className="text-3xl font-bold" data-testid="total-itens">{totalItens}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Em Uso</p>
                <p className="text-3xl font-bold text-amber-600" data-testid="total-em-uso">{totalEmUso}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Disponíveis</p>
                <p className="text-3xl font-bold text-green-600" data-testid="total-disponiveis">{totalDisponiveis}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar item..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-comodato"
            />
          </div>
          <Select value={ativoFilter} onValueChange={setAtivoFilter}>
            <SelectTrigger className="w-36" data-testid="filter-comodato-ativo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="ativo">Ativos</SelectItem>
              <SelectItem value="inativo">Inativos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Items Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-center">Qtd Total</TableHead>
                  <TableHead className="text-center">Em Uso</TableHead>
                  <TableHead className="text-center">Disponíveis</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : filteredItens.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nenhum item encontrado</TableCell></TableRow>
                ) : filteredItens.map(item => (
                  <TableRow 
                    key={item.id} 
                    data-testid={`comodato-row-${item.id}`}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => openDetail(item)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{item.nome}</p>
                          {item.categoria && <p className="text-xs text-muted-foreground">{item.categoria}</p>}
                        </div>
                        <ArrowRight className="h-3 w-3 text-muted-foreground/50 ml-auto" />
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-mono">{item.quantidade_total}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={item.em_uso > 0 ? 'bg-amber-50 text-amber-700 border-amber-200' : ''}>
                        {item.em_uso || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={item.disponiveis > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}>
                        {item.disponiveis || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={item.ativo !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}>
                        {item.ativo !== false ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => handleToggleAtivo(item)} title={item.ativo !== false ? 'Desativar' : 'Ativar'} data-testid={`btn-toggle-comodato-${item.id}`}>
                          <Power className={`h-4 w-4 ${item.ativo !== false ? 'text-green-500' : 'text-gray-400'}`} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)} data-testid={`btn-edit-comodato-${item.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item)} data-testid={`btn-delete-comodato-${item.id}`}>
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Item Dialog */}
        <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
          <DialogContent data-testid="dialog-comodato-item">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Editar Item' : 'Adicionar Item'}</DialogTitle>
              <DialogDescription>Preencha os dados do item de comodato</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Nome do item *</Label>
                <Input
                  value={itemForm.nome}
                  onChange={e => setItemForm(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: Cama hospitalar, Concentrador de O2..."
                  data-testid="input-comodato-nome"
                />
              </div>
              <div className="space-y-2">
                <Label>Quantidade total *</Label>
                <Input
                  type="number"
                  min="0"
                  value={itemForm.quantidade_total}
                  onChange={e => setItemForm(prev => ({ ...prev, quantidade_total: parseInt(e.target.value) || 0 }))}
                  data-testid="input-comodato-qtd"
                />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Input
                  value={itemForm.categoria}
                  onChange={e => setItemForm(prev => ({ ...prev, categoria: e.target.value }))}
                  placeholder="Ex: Equipamentos, Mobiliário..."
                />
              </div>
              <div className="space-y-2">
                <Label>Observações</Label>
                <Input
                  value={itemForm.observacoes}
                  onChange={e => setItemForm(prev => ({ ...prev, observacoes: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveItem} disabled={!itemForm.nome} data-testid="btn-salvar-comodato">{editingItem ? 'Salvar' : 'Adicionar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Item Detail Sheet */}
        <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
          <SheetContent className="sm:max-w-[600px] overflow-y-auto" data-testid="sheet-comodato-detalhe">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                {detailItem?.nome || 'Detalhes do Item'}
              </SheetTitle>
              <SheetDescription>
                {detailItem?.categoria && <span>{detailItem.categoria} — </span>}
                Relatório completo de uso e disponibilidade
              </SheetDescription>
            </SheetHeader>

            {detailLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                Carregando detalhes...
              </div>
            ) : detailData ? (
              <div className="space-y-6 mt-6">
                {/* Stats cards */}
                <div className="grid grid-cols-3 gap-3">
                  <Card>
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-muted-foreground">Total</p>
                      <p className="text-2xl font-bold" data-testid="detail-total">{detailData.item?.quantidade_total || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-muted-foreground">Em Uso</p>
                      <p className="text-2xl font-bold text-amber-600" data-testid="detail-em-uso">{detailData.item?.em_uso || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-muted-foreground">Disponíveis</p>
                      <p className="text-2xl font-bold text-green-600" data-testid="detail-disponiveis">{detailData.item?.disponiveis || 0}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Export button */}
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => handleExportarItem(detailItem?.id)}
                  data-testid="btn-exportar-item"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Exportar relatório deste item
                </Button>

                {/* Tabs: Em uso / Histórico */}
                <Tabs defaultValue="em_uso">
                  <TabsList className="w-full">
                    <TabsTrigger value="em_uso" className="flex-1 gap-1" data-testid="tab-em-uso">
                      Em Uso
                      <Badge variant="secondary" className="ml-1">{detailData.alocacoes_ativas?.length || 0}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="historico" className="flex-1 gap-1" data-testid="tab-historico">
                      Histórico
                      <Badge variant="secondary" className="ml-1">{detailData.historico?.length || 0}</Badge>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="em_uso" className="mt-4">
                    {(!detailData.alocacoes_ativas || detailData.alocacoes_ativas.length === 0) ? (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        Nenhuma alocação ativa para este item
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {detailData.alocacoes_ativas.map((aloc) => (
                          <Card key={aloc.id} data-testid={`aloc-ativa-${aloc.id}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <User className="h-4 w-4 text-muted-foreground" />
                                    <Link
                                      to={`/pacientes/${aloc.internacao_id?.split('/')[0]}/internacao/${aloc.internacao_id}`}
                                      className="font-medium text-sm hover:text-primary hover:underline"
                                      onClick={() => setDetailOpen(false)}
                                    >
                                      {aloc.paciente_nome}
                                    </Link>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Calendar className="h-3 w-3" />
                                    Desde {formatDate(aloc.criado_em)}
                                  </div>
                                  {aloc.observacoes && (
                                    <p className="text-xs text-muted-foreground mt-1">{aloc.observacoes}</p>
                                  )}
                                </div>
                                <Badge className="bg-amber-100 text-amber-800 border-amber-200 shrink-0">
                                  {aloc.quantidade_em_uso} un.
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="historico" className="mt-4">
                    {(!detailData.historico || detailData.historico.length === 0) ? (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        Nenhum histórico para este item
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {detailData.historico.map((aloc) => (
                          <Card key={aloc.id} className="opacity-75" data-testid={`aloc-hist-${aloc.id}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <User className="h-4 w-4 text-muted-foreground" />
                                    <span className="font-medium text-sm">{aloc.paciente_nome}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Calendar className="h-3 w-3" />
                                    {formatDate(aloc.criado_em)} — {formatDate(aloc.ended_at)}
                                  </div>
                                </div>
                                <Badge variant="outline" className="shrink-0">
                                  {aloc.quantidade_em_uso} un.
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            ) : null}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
};

export default Comodato;
